import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Typography,
  TextField,
  Stack,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
} from '@mui/material';
import { Delete, Save, AddCircleOutline } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { CapitalPartner, PlanCapitalAllocation, StockPlan } from '../../../../models/StockPickTypes';
import { emptyAllocation } from '../../../../models/StockPickTypes';
import {
  getCapitalPartnersBrief,
  getPlanAllocations,
  savePlanAllocations,
} from '../../../../services/request/CapitalManagement';
import { getPlanExecution } from '../../../../services/request/planExecutionApi';
import { formatCurrency } from '../../../../utils/CurrencyFormat';

interface Props {
  plan: StockPlan;
  onSaved: () => void;
}

function savedAmountOnPlan(allocations: PlanCapitalAllocation[], partnerId: string): number {
  return allocations
    .filter((a) => a.capitalPartnerId === partnerId)
    .reduce((sum, a) => sum + (a.allocatedAmount || 0), 0);
}

function formTotalForPartner(rows: PlanCapitalAllocation[], partnerId: string): number {
  return rows
    .filter((r) => r.capitalPartnerId === partnerId)
    .reduce((sum, r) => sum + (r.allocatedAmount || 0), 0);
}

function maxAllocatableOnPlan(
  partner: CapitalPartner,
  savedOnThisPlan: PlanCapitalAllocation[],
  partnerId: string
): number {
  const alreadyOnPlan = savedAmountOnPlan(savedOnThisPlan, partnerId);
  const onOtherPlans = (partner.totalAllocated ?? 0) - alreadyOnPlan;
  return (partner.totalContributed ?? 0) - onOtherPlans;
}

const PlanCapitalAllocationsPanel: React.FC<Props> = ({ plan, onSaved }) => {
  const [partners, setPartners] = useState<CapitalPartner[]>([]);
  const [saved, setSaved] = useState<PlanCapitalAllocation[]>([]);
  const [rows, setRows] = useState<PlanCapitalAllocation[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [lockedByPurchase, setLockedByPurchase] = useState(false);
  const [purchaseTotalCost, setPurchaseTotalCost] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    if (!plan.id) return;
    setLoading(true);
    try {
      const [partnerList, allocations, execution] = await Promise.all([
        getCapitalPartnersBrief(),
        getPlanAllocations(plan.id),
        getPlanExecution(plan.id),
      ]);
      setPartners(partnerList);
      const hasPurchase = (execution.purchases?.length ?? 0) > 0 || Boolean(execution.purchase);
      setLockedByPurchase(hasPurchase);
      setPurchaseTotalCost(execution.totalPurchaseCost ?? null);
      const valid = allocations.filter((a) => a.capitalPartnerId && (a.allocatedAmount ?? 0) > 0);
      setSaved(valid);
      setRows(valid.length ? valid.map((a) => ({ ...a })) : [emptyAllocation()]);
      setEditing(!hasPurchase && valid.length === 0);
    } catch {
      toast.error('Failed to load capital allocations');
    } finally {
      setLoading(false);
    }
  }, [plan.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const startEdit = () => {
    if (lockedByPurchase) return;
    setRows(saved.length ? saved.map((a) => ({ ...a })) : [emptyAllocation()]);
    setEditing(true);
  };

  const cancelEdit = () => {
    setRows(saved.length ? saved.map((a) => ({ ...a })) : [emptyAllocation()]);
    setEditing(false);
  };

  const addRow = () => setRows((prev) => [...prev, emptyAllocation()]);

  const removeRow = (idx: number) => {
    setRows((prev) => {
      const next = [...prev];
      next.splice(idx, 1);
      return next.length ? next : [emptyAllocation()];
    });
  };

  const updateRow = (idx: number, patch: Partial<PlanCapitalAllocation>) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const partnerOverLimit = (partnerId: string): boolean => {
    const partner = partners.find((p) => p.id === partnerId);
    if (!partner || !partnerId) return false;
    const max = maxAllocatableOnPlan(partner, saved, partnerId);
    return formTotalForPartner(rows, partnerId) > max + 0.01;
  };

  const handleSave = async () => {
    if (!plan.id || lockedByPurchase) return;
    const payload = rows.filter((r) => r.capitalPartnerId && r.allocatedAmount > 0);
    const partnerIds = Array.from(new Set(payload.map((r) => r.capitalPartnerId)));
    if (partnerIds.some((id) => partnerOverLimit(id))) {
      toast.error('One or more allocations exceed the partner available balance');
      return;
    }
    setSaving(true);
    try {
      const updated = await savePlanAllocations(plan.id, payload);
      const valid = updated.filter((a) => a.capitalPartnerId && (a.allocatedAmount ?? 0) > 0);
      setSaved(valid);
      setRows(valid.length ? valid.map((a) => ({ ...a })) : [emptyAllocation()]);
      setEditing(valid.length === 0);
      toast.success('Capital allocations saved');
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const total = editing
    ? rows.reduce((s, r) => s + (r.allocatedAmount || 0), 0)
    : saved.reduce((s, r) => s + (r.allocatedAmount || 0), 0);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">Loading partner allocations…</Typography>
      </Paper>
    );
  }

  if (lockedByPurchase) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Partner capital (from purchase)
        </Typography>
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          Amounts are set when you record the stock purchase on the <strong>Execution</strong> tab. Each
          partner&apos;s share equals their portion of the purchase cost
          {purchaseTotalCost != null ? ` (${formatCurrency(purchaseTotalCost)} total)` : ''}.
        </Alert>
        {saved.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No partner shares on the purchase yet — edit the purchase on Execution and add partners.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Partner</TableCell>
                <TableCell align="right">Share of purchase cost</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {saved.map((row) => (
                <TableRow key={row.id ?? `${row.capitalPartnerId}-${row.allocatedAmount}`}>
                  <TableCell>{row.partnerName || partners.find((p) => p.id === row.capitalPartnerId)?.name || '—'}</TableCell>
                  <TableCell align="right">{formatCurrency(row.allocatedAmount)}</TableCell>
                  <TableCell>{row.note?.trim() || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Chip label={`Total: ${formatCurrency(total)}`} size="small" color="primary" variant="outlined" sx={{ mt: 2 }} />
      </Paper>
    );
  }

  if (!editing && saved.length > 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="primary">Partner capital (pre-purchase)</Typography>
          <Button size="small" onClick={startEdit}>Edit allocations</Button>
        </Stack>
        <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
          Once a purchase is recorded on Execution, these manual rows are replaced by purchase-based partner shares.
        </Alert>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Partner</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {saved.map((row) => (
              <TableRow key={row.id ?? `${row.capitalPartnerId}-${row.allocatedAmount}`}>
                <TableCell>{row.partnerName || partners.find((p) => p.id === row.capitalPartnerId)?.name || '—'}</TableCell>
                <TableCell align="right">{formatCurrency(row.allocatedAmount)}</TableCell>
                <TableCell>{row.note?.trim() || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Chip label={`Total allocated: ${formatCurrency(total)}`} size="small" color="primary" variant="outlined" sx={{ mt: 2 }} />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="primary">Partner capital (pre-purchase)</Typography>
        <Button size="small" startIcon={<AddCircleOutline />} onClick={addRow}>Add partner share</Button>
      </Stack>
      <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
        Optional before execution. After you record a purchase, partner amounts come from the purchase cost on{' '}
        <strong>Execution</strong>. Capture partner money under{' '}
        <Link component={RouterLink} to="/admin/capitals/partners">Capital Partners</Link>.
      </Alert>
      <Stack spacing={1.5}>
        {rows.map((row, idx) => {
          const partner = partners.find((p) => p.id === row.capitalPartnerId);
          const maxOnPlan = partner && row.capitalPartnerId
            ? maxAllocatableOnPlan(partner, saved, row.capitalPartnerId)
            : 0;
          const overLimit = row.capitalPartnerId ? partnerOverLimit(row.capitalPartnerId) : false;
          return (
            <Stack key={row.id ?? idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
              <TextField
                select
                label="Capital partner"
                value={row.capitalPartnerId ?? ''}
                onChange={(e) => updateRow(idx, { capitalPartnerId: e.target.value })}
                sx={{ minWidth: 220, flex: 1 }}
                size="small"
              >
                <MenuItem value=""><em>Select partner</em></MenuItem>
                {partners.map((p) => {
                  const availForPlan = maxAllocatableOnPlan(p, saved, p.id!);
                  return (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} (up to {formatCurrency(availForPlan)} on this plan)
                    </MenuItem>
                  );
                })}
              </TextField>
              <TextField
                label="Amount (INR)"
                type="number"
                value={row.allocatedAmount || ''}
                onChange={(e) => updateRow(idx, { allocatedAmount: Number(e.target.value) || 0 })}
                size="small"
                sx={{ width: 140 }}
                error={overLimit}
                helperText={partner && row.capitalPartnerId && overLimit
                  ? `Max ${formatCurrency(maxOnPlan)} for ${partner.name} on this plan`
                  : undefined}
              />
              <TextField
                label="Note"
                value={row.note ?? ''}
                onChange={(e) => updateRow(idx, { note: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
              />
              <IconButton color="error" onClick={() => removeRow(idx)} disabled={rows.length <= 1 && !saved.length}>
                <Delete />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Chip label={`Total allocated: ${formatCurrency(total)}`} size="small" color="primary" variant="outlined" />
        <Stack direction="row" spacing={1}>
          {saved.length > 0 && (
            <Button size="small" onClick={cancelEdit}>Cancel</Button>
          )}
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save allocations'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default PlanCapitalAllocationsPanel;
